import { type ReactElement, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from './analytics';
import { captureAttributionFromCurrentVisit } from './attribution';
import type { AuthSession } from '@/auth/types';
import type { AnalyticsEventName } from '@flowqa/shared';

type AnalyticsControllerProps = {
  session: AuthSession | null;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
};

const routeViewEvents: Array<{ pattern: RegExp; event: AnalyticsEventName }> = [
  { pattern: /^\/$/, event: 'landing_page_viewed' },
  { pattern: /^\/plans\/?$/, event: 'pricing_viewed' },
  { pattern: /^\/preview\/?$/, event: 'preview_viewed' },
  { pattern: /^\/use-cases(?:\/|$)/, event: 'use_cases_viewed' },
  { pattern: /^\/sign-up(?:\/|$)/, event: 'signup_started' },
  { pattern: /^\/execution-runs\/?$/, event: 'dashboard_viewed' },
  { pattern: /^\/suites\/[^/]+\/?$/, event: 'suite_viewed' },
  { pattern: /^\/tests\/[^/]+\/?$/, event: 'test_viewed' },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/execution-runs\/?$/,
    event: 'dashboard_viewed',
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/suites\/[^/]+\/?$/,
    event: 'suite_viewed',
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/tests\/[^/]+\/?$/,
    event: 'test_viewed',
  },
];

const resolveRouteEvent = (pathname: string): AnalyticsEventName | null =>
  routeViewEvents.find((route) => route.pattern.test(pathname))?.event ?? null;

const signupStartedStorageKey = 'flowqa.analytics.signupStarted';

const markSignupStarted = (): void => {
  try {
    window.sessionStorage.setItem(signupStartedStorageKey, 'true');
  } catch {
    // Analytics state is best-effort only.
  }
};

const consumeSignupStarted = (): boolean => {
  try {
    const hasSignupStarted = window.sessionStorage.getItem(signupStartedStorageKey) === 'true';
    if (hasSignupStarted) {
      window.sessionStorage.removeItem(signupStartedStorageKey);
    }
    return hasSignupStarted;
  } catch {
    return false;
  }
};

export const AnalyticsController = ({
  session,
  selectedOrganisationId,
  selectedProjectId,
}: AnalyticsControllerProps): ReactElement | null => {
  const location = useLocation();
  const lastCapturedRouteRef = useRef<string | null>(null);
  const userId = session?.userId ?? null;
  const organisationId = selectedOrganisationId ?? session?.orgId ?? null;
  const userProperties = useMemo(
    () => ({
      organisationId,
      lastOrganisationId: session?.lastOrganisationId ?? null,
      lastProjectId: session?.lastProjectId ?? null,
    }),
    [organisationId, session?.lastOrganisationId, session?.lastProjectId],
  );

  useEffect(() => {
    captureAttributionFromCurrentVisit();
    analytics.initialise();
  }, []);

  useEffect(() => {
    if (!userId) {
      return;
    }
    analytics.identify(userId, userProperties);
    if (consumeSignupStarted()) {
      analytics.capture('signup_completed', {
        organisationId,
        authProvider: 'clerk',
      });
    }
  }, [organisationId, userId, userProperties]);

  useEffect(() => {
    const eventName = resolveRouteEvent(location.pathname);
    if (eventName === null) return;
    const routeKey = `${eventName}:${location.pathname}`;
    if (lastCapturedRouteRef.current === routeKey) return;
    lastCapturedRouteRef.current = routeKey;
    if (eventName === 'signup_started') {
      markSignupStarted();
    }
    analytics.capture(eventName, {
      path: location.pathname,
      organisationId,
      projectId: selectedProjectId,
    });
  }, [location.pathname, organisationId, selectedProjectId]);

  return null;
};
